import React from 'react'
import { useQuery } from '@apollo/client'
import { PageSection as Section, PageHeader, Grid } from '@superhi/design'
import { useRouter } from 'next/router'

import NoResults from '../../components/NoResults'
import { API } from '../../api'
import Page from '../../components/Page'
import CourseCollectionCard from '../../components/CourseCollectionCard'
import SEO from '../../components/SEO'
import Results from '../../components/Results'
import Loading from '../../components/Loading'
import { PageConfig } from '../../routing/types'
import Data from '../../components/Data'

import { COLLECTIONS } from './queries'
import WhyCollections from './components/WhyCollections'
import ROUTE from './route'
import { Props } from './data'

const PER_PAGE_DEFAULT = 30

type FiltersConfig = {
  courseId: string | undefined
}

const GRID_1_CELL_SPAN_1: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: [2, 7],
  large: 6,
}
const GRID_1_CELL_SPAN_2: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: 4,
  large: 6,
}

const Collections: PageConfig<Props> = ({ productCourseIdOptions }) => {
  const router = useRouter()
  const perPage = (router.query.perPage as unknown as number) || PER_PAGE_DEFAULT

  const { data, loading } = useQuery<
    API.Collections_collections,
    API.Collections_collectionsVariables
  >(COLLECTIONS, {
    variables: {
      productCourseId: router.query.courseId as unknown as string,
      page: router.query.page as unknown as number,
      perPage,
    },
    ssr: false,
  })

  return (
    <Page>
      <SEO
        title="Learning paths"
        description="Let our carefully curated mix of courses guide you from beginning to end, to achieve your goals. Choose one of ours or make your own combination of courses, and get a discount."
      />

      <PageHeader
        title="Learning paths"
        subtitle="Let our carefully curated mix of courses guide you from beginning to end, to achieve your goals. Choose one of ours or make your own combination of courses, and get a discount."
        backgroundVersion="BLUE_02"
      />

      <Results<FiltersConfig>
        name="collections-results"
        numResults={data?.productCollections?.totalCount}
        filters={{ courseId: productCourseIdOptions }}
        query={router.query}
        perPage={perPage}
        isLoading={loading}
      >
        <Section>
          <Grid alignItems="stretch">
            <Data
              data={data?.productCollections?.nodes!}
              loading={loading}
              whenLoading={
                <Grid.Cell span="full">
                  <Loading />
                </Grid.Cell>
              }
              whenEmpty={
                <Grid.Cell span="full">
                  <NoResults hAlign="center" />
                </Grid.Cell>
              }
            >
              {(collections) => (
                <>
                  {collections.map((collection, i) => (
                    <Grid.Cell key={collection.id} span={GRID_1_CELL_SPAN_1}>
                      <CourseCollectionCard {...(collection as any)} imageLazy={i > 1} />
                    </Grid.Cell>
                  ))}
                </>
              )}
            </Data>
          </Grid>
        </Section>
      </Results>

      <WhyCollections />
    </Page>
  )
}

Collections.route = ROUTE

export { getStaticProps } from './data'
export default Collections
